'use strict';

// LP機能タブ切替。aria-selected + パネル表示 + ロービングtabindex + 矢印/Home/End操作。
// スマホ等タッチデバイスでは、パネルの横スワイプでも前後の機能へ切り替えられる。
// 切替時は移動方向に応じたスライド＋フェードアニメーションを付ける（SCSS: _features.scss）。
//
// 期待するマークアップ:
//   .lp-tabs[role=tablist]
//     button.lp-tab[role=tab][data-tab="t1"][aria-controls="t1"][id="tab-t1"][aria-selected][tabindex]
//   .lp-tabpanel[role=tabpanel][id="t1"][aria-labelledby="tab-t1"]（表示時 .is-active / 方向 data-anim）
(function() {
  const SWIPE_THRESHOLD = 45; // 横移動がこの px 以上でスワイプ成立
  const SWIPE_RATIO = 1.4; // |dx| が |dy| のこの倍以上のときだけ横スワイプと判定（縦スクロール優先）

  function currentIndex(tabs) {
    return tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
  }

  // index のタブ／パネルをアクティブにする。
  // options.direction: 'next' | 'prev'（アニメーション方向）／options.focusTab: タブにフォーカス
  function activate(tabs, panels, index, options) {
    const opts = options || {};
    tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel) => {
      panel.classList.remove('is-active');
      panel.removeAttribute('data-anim');
    });

    const activeTab = tabs[index];
    const panel = document.getElementById(activeTab.dataset.tab);
    if (panel) {
      if (opts.direction) panel.setAttribute('data-anim', opts.direction);
      panel.classList.add('is-active');
    }
    if (opts.focusTab) activeTab.focus();
  }

  // タッチでの横スワイプを検出して onSwipe(step) を呼ぶ（step: 左スワイプ=+1 / 右スワイプ=-1）
  function bindSwipe(panels, onSwipe) {
    panels.forEach((panel) => {
      let startX = 0;
      let startY = 0;
      let tracking = false;

      panel.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) {
          tracking = false;
          return;
        }
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = true;
      }, { passive: true });

      panel.addEventListener('touchend', (e) => {
        if (!tracking) return;
        tracking = false;
        const touch = e.changedTouches[0];
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;
        if (Math.abs(dx) < SWIPE_THRESHOLD) return;
        if (Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO) return; // 縦方向の動きが優位ならスクロールに任せる
        onSwipe(dx < 0 ? 1 : -1);
      }, { passive: true });
    });
  }

  // 非アクティブパネル（display:none）の lazy 画像はタブ表示時まで読み込まれず、
  // 切替の瞬間にチラつく。セクションが画面に近づいた時点で全パネル画像を先読みし、
  // HTTPキャッシュを温めておくことで切替を即時にする。
  function preloadPanelImages(panels) {
    panels.forEach((panel) => {
      const img = panel.querySelector('img');
      if (!img) return;
      const src = img.currentSrc || img.getAttribute('src');
      if (!src) return;
      const warm = new Image();
      warm.src = src;
    });
  }

  function setupImagePreload(tablist, panels) {
    const section = tablist.closest('.lp-section');
    if (!section || !('IntersectionObserver' in window)) {
      preloadPanelImages(panels);
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        preloadPanelImages(panels);
        obs.disconnect();
      });
    }, { rootMargin: '200px' });
    io.observe(section);
  }

  function initTablist(tablist) {
    const tabs = Array.from(tablist.querySelectorAll('.lp-tab'));
    if (tabs.length === 0) return;

    const panels = tabs
      .map((tab) => document.getElementById(tab.dataset.tab))
      .filter(Boolean);

    setupImagePreload(tablist, panels);

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => {
        const direction = i > currentIndex(tabs) ? 'next' : 'prev';
        activate(tabs, panels, i, { direction });
      });
      tab.addEventListener('keydown', (e) => {
        let next = null;
        if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next !== null) {
          e.preventDefault();
          activate(tabs, panels, next, { focusTab: true, direction: next > i ? 'next' : 'prev' });
        }
      });
    });

    bindSwipe(panels, (step) => {
      const cur = currentIndex(tabs);
      if (cur === -1) return;
      const next = cur + step;
      if (next < 0 || next >= tabs.length) return; // 端ではループせず何もしない
      activate(tabs, panels, next, { direction: step > 0 ? 'next' : 'prev' });
      tabs[next].scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }

  function init() {
    document.querySelectorAll('.lp-tabs[role="tablist"]').forEach(initTablist);
  }

  document.addEventListener('turbolinks:load', init);
})();
